import { Col, Divider, Form, Row } from 'antd';
import { cloneDeep } from 'es-toolkit';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { LD_CLAIM_DUPLICATE_STATUS, LD_CLAIM_DUPLICATE_STATUS_MESSAGE } from '../../../../../../contants/ClaimsEnums';
import {
  updateLossAndDamageClaimDetailsInProgress,
  validateDuplicateClaimInProgress,
} from '../../../../../../redux/Claims/LossAndDamage/LossAndDamageClaimsActions';
import { setAppLoader } from '../../../../../../redux/common/CommonActions';
import { routeFileSelector, valueToString } from '../../../../../../utilz';
import { LADCovertDatesFromMomentToAPIType } from '../../../../../../utilz/PreClaimsUtils';
import Button from '../../../../../components/Buttons/Button';
import SaveCancelButton from '../../../../../components/Common/SaveCancelButton';
import PrimaryLabel from '../../../../../components/Labels/PrimaryLabel';
import ConfirmationModal from '../../../../../components/Modals/ConfirmationModal';
import LADClaimDetailsForm from './LADClaimDetailsForm';

const LADClaimsDetailsTab = () => {
  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);
  const dispatch = useDispatch();
  const [editMode, setEditMode] = useState(false);
  const [form] = Form.useForm();
  const { claimDetails } = useSelector((state) => state.lossAndDamage);
  const [duplicateMessage, setDuplicateMessage] = useState('');
  const [isInstantDuplicateValidation, setIsInstantDuplicateValidation] = useState(false);
  const claimFormDataRef = useRef(null);
  const invoiceAndFbNoRef = useRef({
    invoiceNo: undefined,
    claimProNo: undefined,
  });

  useEffect(() => {
    if (claimDetails?.data?.claimNumber) {
      form.setFieldsValue(claimDetails?.data);
    }
  }, [claimDetails?.data]);

  const handleCancel = () => {
    form.setFieldsValue(claimDetails?.data);
    setEditMode(false);
  };

  const initiateUpdateClaimDetails = (payload) => {
    payload.handleDone = () => {
      setEditMode(false);
    };
    dispatch(updateLossAndDamageClaimDetailsInProgress(payload));
  };

  const handleSubmit = async (data) => {
    try {
      let payload = cloneDeep(claimDetails?.data);
      payload = { ...payload, ...data };
      payload = LADCovertDatesFromMomentToAPIType(payload);

      dispatch(setAppLoader(true));
      const handleDone = (duplicateStatus) => {
        dispatch(setAppLoader(false));
        if (Number(duplicateStatus) === LD_CLAIM_DUPLICATE_STATUS.NEW) {
          initiateUpdateClaimDetails(payload);
          return;
          // biome-ignore lint/style/noUselessElse: <explanation>
        } else {
          setDuplicateMessage(LD_CLAIM_DUPLICATE_STATUS_MESSAGE[duplicateStatus]);
          claimFormDataRef.current = payload;
        }
      };
      const checkDuplicatePayload = {
        claimKey: claimDetails.data?.claimKey,
        freightBillNo: valueToString(payload.claimProNo),
        invoiceNo: valueToString(payload.invoiceNo),
        handleDone,
        isInstantValidation: true,
      };
      dispatch(validateDuplicateClaimInProgress(checkDuplicatePayload));
    } catch {
      dispatch(setAppLoader(false));
    }
  };

  const handleDuplicateApprove = () => {
    if (!isInstantDuplicateValidation) initiateUpdateClaimDetails(claimFormDataRef.current);
    handleDuplicateCancel();
  };

  const handleDuplicateCancel = () => {
    setDuplicateMessage(false);
    setIsInstantDuplicateValidation(false);
    claimFormDataRef.current = null;
  };

  const checkInstantClaimsDuplicate = (changedFieldName) => async () => {
    try {
      const basicDetails = form.getFieldsValue();
      if (invoiceAndFbNoRef.current[changedFieldName] === basicDetails[changedFieldName]) {
        return;
      }
      invoiceAndFbNoRef.current = {
        invoiceNo: basicDetails.invoiceNo,
        claimProNo: basicDetails.claimProNo,
      };
      const handleDone = (duplicateStatus) => {
        if (Number(duplicateStatus) !== LD_CLAIM_DUPLICATE_STATUS.NEW) {
          setIsInstantDuplicateValidation(true);
          setDuplicateMessage(LD_CLAIM_DUPLICATE_STATUS_MESSAGE[duplicateStatus]);
        } else {
          setIsInstantDuplicateValidation(false);
        }
      };
      const payload = {
        claimKey: claimDetails.data?.claimKey,
        freightBillNo: valueToString(basicDetails.claimProNo),
        invoiceNo: valueToString(basicDetails.invoiceNo),
        handleDone,
        isInstantValidation: true,
      };
      dispatch(validateDuplicateClaimInProgress(payload));
    } catch (error) {
      console.log(error);
      setIsInstantDuplicateValidation(false);
    }
  };

  return (
    <>
      <Form onFinish={handleSubmit} className="h-100 d-flex flex-direction-column" form={form}>
        <div className="py-10 px-30">
          <Row justify="space-between" align="middle">
            <Col>
              <PrimaryLabel>Details</PrimaryLabel>
            </Col>
            <Col>
              {!editMode ? (
                <Button onClick={() => setEditMode(true)}>Edit</Button>
              ) : (
                <SaveCancelButton editMode onCancel={handleCancel} />
              )}
            </Col>
          </Row>
        </div>
        <div>
          <Divider className="m-0 border-bottom-border" />
        </div>
        <div className="px-30 py-10 flex-1 custom-scroll-bar">
          <LADClaimDetailsForm
            editMode={editMode}
            form={form}
            preparedBy={claimDetails?.data?.createdByUser}
            checkInstantClaimsDuplicate={checkInstantClaimsDuplicate}
          />
        </div>
      </Form>
      <ConfirmationModal
        open={Boolean(duplicateMessage)}
        onClose={handleDuplicateCancel}
        onOk={handleDuplicateApprove}
        onCancel={handleDuplicateCancel}
        description={duplicateMessage}
        title={t('translation:default.fields.alert')}
        cancelButton={isInstantDuplicateValidation}
      />
    </>
  );
};

export default LADClaimsDetailsTab;
